
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  Search, Plus, Filter, Download, CreditCard, 
  RefreshCw, Layers, X, Save, Loader2, RotateCcw,
  CheckCircle2, AlertCircle, Clock, ChevronRight,
  PlusCircle, Trash2, Tag, Receipt, Banknote,
  Calendar, ArrowDownLeft, Building2, ChevronDown, 
  FileSpreadsheet, User as UserIcon, Hash, DollarSign, Eye, ImageIcon, Edit3, Trash, ExternalLink, ZoomIn
} from 'lucide-react';
import { InvoiceStatus, Client, Order, User, FixedExpense, Payment } from './types';
import { Card, Badge } from './SharedComponents';
import { db } from './supabase';

interface PaymentsPageProps {
  clients: Client[];
  orders: Order[];
  user: User;
  addNotification: (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

export const PaymentsPage = ({ clients, orders, user, addNotification }: PaymentsPageProps) => {
  const [search, setSearch] = useState('');
  const [isNewPaymentOpen, setIsNewPaymentOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any | null>(null);
  const [editingPaymentData, setEditingPaymentData] = useState<any | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [viewingFileUrl, setViewingFileUrl] = useState<string | null>(null);

  // Mock de Pagamentos (Saídas/Débitos) - Removido dados simulados conforme pedido
  const [payments, setPayments] = useState<Payment[]>([]);

  const [activeTab, setActiveTab] = useState<'general' | 'fixed'>('general');
  const [fixedExpenses, setFixedExpenses] = useState<FixedExpense[]>([]);
  const [isNewFixedExpenseOpen, setIsNewFixedExpenseOpen] = useState(false);
  const [editingFixedExpenseData, setEditingFixedExpenseData] = useState<FixedExpense | null>(null);
  const [selectedFixedExpense, setSelectedFixedExpense] = useState<FixedExpense | null>(null);

  useEffect(() => {
    const loadFixedExpenses = async () => {
      if (!user.companyId) return;
      try {
        const data = await db.getFixedExpenses(user.companyId);
        setFixedExpenses(data);
      } catch (error) {
        console.error("Erro ao carregar gastos fixos:", error);
      }
    };

    if (activeTab === 'fixed') {
      loadFixedExpenses();
    }
  }, [activeTab, user.companyId]);

  useEffect(() => {
    const loadPayments = async () => {
      if (!user.companyId) return;
      try {
        const data = await db.getPayments(user.companyId);
        setPayments(data);
      } catch (error) {
        console.error("Erro ao carregar pagamentos:", error);
      }
    };

    if (activeTab === 'general') {
      loadPayments();
    }
  }, [activeTab, user.companyId]);

  const filteredPayments = useMemo(() => {
    return payments.filter(p => {
      const matchesSearch = p.paymentNum.toLowerCase().includes(search.toLowerCase()) ||
                          p.clientName.toLowerCase().includes(search.toLowerCase()) ||
                          (p.invoiceNum || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !statusFilter || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [payments, search, statusFilter]);

  const stats = useMemo(() => ({
    totalPaid: payments.filter(p => p.status === 'PAID').reduce((a, b) => a + b.amount, 0),
    totalPending: payments.filter(p => p.status === 'PENDING').reduce((a, b) => a + b.amount, 0),
  }), [payments]);

  const handleExportCSV = () => {
    setIsExporting(true);
    const rows = [
      ["sep=;"],
      ["RELATÓRIO DE PAGAMENTOS (SAÍDAS) - EVENTLOG"],
      ["Gerado em", new Date().toLocaleString('pt-BR')],
      [""],
      ["Pagamento #", "Fatura #", "Método", "ID Transação", "Favorecido", "Valor (R$)", "Data Pagto", "Status"]
    ];
    filteredPayments.forEach(p => {
      rows.push([
        p.paymentNum,
        p.invoiceNum || '',
        p.method,
        p.transactionId || '',
        p.clientName,
        p.amount.toFixed(2).replace('.', ','),
        p.date.split('-').reverse().join('/'),
        p.status === 'PAID' ? 'LIQUIDADO' : 'PENDENTE'
      ]);
    });
    const csvString = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const csvContent = new TextEncoder().encode(csvString);
    const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `EventLog_Pagamentos_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    setTimeout(() => {
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setIsExporting(false);
    }, 1000);
  };

  const handleSavePayment = async (payment: Payment) => {
    if (!user.companyId) return;
    
    try {
      // Garante que o ID seja um UUID válido ou gera um novo
      const isUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
      
      const paymentToSave: Payment = {
        ...payment,
        company_id: user.companyId,
        id: isUUID(payment.id) ? payment.id : crypto.randomUUID(),
        createdAt: payment.createdAt || new Date().toISOString()
      };

      console.log("Tentando salvar pagamento:", paymentToSave);
      await db.savePayment(paymentToSave);

      if (editingPaymentData) {
        setPayments(prev => prev.map(p => p.id === paymentToSave.id ? paymentToSave : p));
        setEditingPaymentData(null);
        addNotification("Sucesso", "Pagamento atualizado com sucesso.", "success");
      } else {
        setPayments([paymentToSave, ...payments]);
        addNotification("Sucesso", "Pagamento registrado com sucesso.", "success");
      }
      setIsNewPaymentOpen(false);
      setSelectedPayment(null);
    } catch (error: any) {
      console.error("Erro detalhado ao salvar pagamento:", error);
      addNotification("Erro", `Erro ao salvar pagamento: ${error.message || 'Erro desconhecido'}`, "error");
    }
  };

  const handleDeletePayment = async (id: string) => {
    try {
      await db.deletePayment(id);
      setPayments(prev => prev.filter(p => p.id !== id));
      setSelectedPayment(null);
      addNotification("Sucesso", "Pagamento excluído com sucesso.", "success");
    } catch (error) {
      console.error("Erro ao excluir pagamento:", error);
      addNotification("Erro", "Erro ao excluir pagamento.", "error");
    }
  };

  const handleEditClick = (payment: any) => {
    setEditingPaymentData(payment);
    setSelectedPayment(null); 
    setIsNewPaymentOpen(true);
  };

  const handleOpenFile = (paymentNum: string) => {
    setViewingFileUrl("https://images.unsplash.com/photo-1554224155-1696413565d3?auto=format&fit=crop&w=800&w=1200");
  };

  const handleSaveFixedExpense = async (expense: FixedExpense) => {
    if (!user.companyId) return;
    
    try {
      // Garante que o ID seja um UUID válido ou gera um novo
      const isUUID = (id: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

      const expenseToSave: FixedExpense = {
        ...expense,
        company_id: user.companyId,
        id: isUUID(expense.id) ? expense.id : crypto.randomUUID(),
        createdAt: expense.createdAt || new Date().toISOString(),
        status: 'ACTIVE' as const
      };

      console.log("Tentando salvar gasto fixo:", expenseToSave);
      await db.saveFixedExpense(expenseToSave);
      
      if (editingFixedExpenseData) {
        setFixedExpenses(prev => prev.map(e => e.id === expenseToSave.id ? expenseToSave : e));
        setEditingFixedExpenseData(null);
        addNotification("Sucesso", "Gasto fixo atualizado com sucesso.", "success");
      } else {
        setFixedExpenses([expenseToSave, ...fixedExpenses]);
        addNotification("Sucesso", "Gasto fixo registrado com sucesso.", "success");
      }
      setIsNewFixedExpenseOpen(false);
      setSelectedFixedExpense(null);
    } catch (error: any) {
      console.error("Erro detalhado ao salvar gasto fixo:", error);
      addNotification("Erro", `Erro ao salvar gasto fixo: ${error.message || 'Erro desconhecido'}`, "error");
    }
  };

  const handleDeleteFixedExpense = async (id: string) => {
    try {
      await db.deleteFixedExpense(id);
      setFixedExpenses(prev => prev.filter(e => e.id !== id));
      setSelectedFixedExpense(null);
      addNotification("Sucesso", "Gasto fixo excluído com sucesso.", "success");
    } catch (error) {
      addNotification("Erro", "Erro ao excluir gasto fixo.", "error");
    }
  };

  const handleEditFixedClick = (expense: any) => {
    setEditingFixedExpenseData(expense);
    setSelectedFixedExpense(null);
    setIsNewFixedExpenseOpen(true);
  };

  const handleLiquidatePayment = async (payment: Payment) => {
    if (!user.companyId) return;
    
    try {
      const updatedPayment: Payment = {
        ...payment,
        status: 'PAID'
      };

      console.log("Liquidando pagamento:", updatedPayment);
      await db.savePayment(updatedPayment);
      
      setPayments(prev => prev.map(p => p.id === updatedPayment.id ? updatedPayment : p));
      setSelectedPayment(null);
      addNotification("Sucesso", "Pagamento liquidado com sucesso.", "success");
    } catch (error: any) {
      console.error("Erro ao liquidar pagamento:", error);
      addNotification("Erro", "Erro ao liquidar pagamento.", "error");
    }
  };

  return (
    <div className="p-4 md:p-10 space-y-8 animate-in fade-in duration-500 pb-20 min-h-screen text-secondary">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
        <div className="space-y-1">
           <div className="flex items-center gap-2 text-red-500 font-black uppercase text-[10px] tracking-[0.4em]">
              <ArrowDownLeft size={14} strokeWidth={3} /> Financeiro / Infraestrutura
           </div>
           <h1 className="text-3xl md:text-4xl font-black text-secondary uppercase tracking-tighter leading-none">Gestão de Pagamentos</h1>
        </div>
        
        <div className="flex items-center gap-4 w-full lg:w-auto justify-between lg:justify-end border-t lg:border-none pt-6 lg:pt-0 border-gray-100">
           <div className="flex flex-col items-start lg:items-end gap-1">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Liquidado</span>
              <Badge className="bg-green-50 text-green-600 border-green-100 font-black px-5 py-2 shadow-sm text-xs md:text-sm">
                 R$ {stats.totalPaid.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Badge>
           </div>
           <div className="flex flex-col items-start lg:items-end gap-1">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Pendente</span>
              <Badge className="bg-red-50 text-red-600 border-red-100 font-black px-5 py-2 shadow-sm text-xs md:text-sm">
                 R$ {stats.totalPending.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </Badge>
           </div>
        </div>
      </div>

      <div className="flex border-b border-gray-100 overflow-x-auto scrollbar-hide">
        <button 
          onClick={() => setActiveTab('general')}
          className={`px-8 py-4 text-[10px] font-black uppercase tracking-widest border-b-4 transition-all whitespace-nowrap ${
            activeTab === 'general' 
              ? 'border-red-600 text-red-600 bg-red-50/50' 
              : 'border-transparent text-gray-400 hover:text-secondary hover:bg-gray-50'
          }`}
        >
          Geral
        </button>
        <button 
          onClick={() => setActiveTab('fixed')}
          className={`px-8 py-4 text-[10px] font-black uppercase tracking-widest border-b-4 transition-all whitespace-nowrap ${
            activeTab === 'fixed' 
              ? 'border-red-600 text-red-600 bg-red-50/50' 
              : 'border-transparent text-gray-400 hover:text-secondary hover:bg-gray-50'
          }`}
        >
          Pagamentos Fixos
        </button>
      </div>

      {activeTab === 'general' ? (
        <>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/50 p-4 rounded-[32px] border border-gray-100 shadow-sm">
            <div className="flex gap-2 w-full md:w-auto">
               <button 
                onClick={() => { setEditingPaymentData(null); setIsNewPaymentOpen(true); }} 
                className="flex-1 md:flex-none bg-red-600 text-white px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-red-700 transition-all shadow-xl shadow-red-600/20 active:scale-95"
               >
                  <Plus size={18} /> Novo Lançamento
               </button>
               <button 
                onClick={handleExportCSV}
                disabled={isExporting}
                className="flex-1 md:flex-none bg-white border border-gray-200 text-gray-400 px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-gray-50 transition-all disabled:opacity-50"
               >
                  {isExporting ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                  Excel
               </button>
            </div>
            
            <div className="relative w-full md:w-96 group">
               <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-red-500 transition-colors" size={20} />
               <input 
                 placeholder="Filtrar por ID, Favorecido ou Fatura..." 
                 className="w-full pl-14 pr-6 py-4 bg-white border border-gray-100 rounded-[22px] shadow-sm outline-none text-xs font-bold text-secondary focus:border-red-500/40 transition-all placeholder:text-gray-300" 
                 value={search} 
                 onChange={e => setSearch(e.target.value)} 
               />
            </div>
          </div>

          <Card className="bg-white border border-gray-100 shadow-xl rounded-[32px] overflow-hidden">
             <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full text-left border-collapse min-w-[900px]">
                   <thead>
                      <tr className="bg-gray-50 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] border-b border-gray-100">
                         <th className="px-8 py-6">ID Pagamento</th>
                         <th className="px-4 py-6">Fatura #</th>
                         <th className="px-8 py-6">Método / Canal</th>
                         <th className="px-8 py-6">Favorecido</th>
                         <th className="px-8 py-6 text-right">Quantia</th>
                         <th className="px-8 py-6 text-center">Vencimento</th>
                         <th className="px-8 py-6 text-right">Ação</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-50">
                      {filteredPayments.length > 0 ? filteredPayments.map(pay => (
                        <tr key={pay.id} className="hover:bg-primary/5 transition-all group">
                           <td className="px-8 py-6">
                              <div className="flex flex-col gap-1.5 items-start">
                                <p className="text-xs font-black text-secondary uppercase tracking-tight">{pay.paymentNum}</p>
                                <Badge className={pay.status === 'PAID' ? 'bg-green-50 text-green-600 border-none text-[8px]' : 'bg-amber-50 text-amber-600 border-none text-[8px]'}>
                                   {pay.status === 'PAID' ? 'LIQUIDADO' : 'PENDENTE'}
                                </Badge>
                              </div>
                           </td>
                           <td className="px-4 py-6">
                              <span className="text-[10px] font-black text-gray-300 uppercase tracking-widest">{pay.invoiceNum || '---'}</span>
                           </td>
                           <td className="px-8 py-6">
                              <div className="flex items-center gap-3">
                                 <div className="p-2 bg-gray-50 rounded-xl text-red-500 shadow-inner"><CreditCard size={16} /></div>
                                 <span className="text-[10px] font-black uppercase text-secondary">{pay.method}</span>
                              </div>
                           </td>
                           <td className="px-8 py-6">
                              <p className="text-xs font-black text-secondary uppercase truncate max-w-[200px]">{pay.clientName}</p>
                              <p className="text-[9px] font-mono text-gray-300 mt-1 uppercase truncate">{pay.transactionId}</p>
                           </td>
                           <td className="px-8 py-6 text-right">
                              <p className="font-black text-red-600 text-sm">R$ {pay.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                           </td>
                           <td className="px-8 py-6 text-center">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                {pay.date.split('-').reverse().join('/')}
                              </span>
                           </td>
                           <td className="px-8 py-6 text-right">
                              <button 
                                onClick={() => setSelectedPayment(pay)}
                                className="p-2.5 text-gray-300 hover:text-primary hover:bg-white rounded-xl transition-all active:scale-90 shadow-sm border border-transparent hover:border-gray-100"
                              >
                                <ChevronRight size={20} />
                              </button>
                           </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={7} className="px-8 py-24 text-center text-gray-200 font-black uppercase tracking-[0.3em] italic bg-transparent">Nenhum débito no radar</td></tr>
                      )}
                   </tbody>
                </table>
             </div>
          </Card>
        </>
      ) : (
        <>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/50 p-4 rounded-[32px] border border-gray-100 shadow-sm">
            <div className="flex gap-2 w-full md:w-auto">
               <button 
                onClick={() => { setEditingFixedExpenseData(null); setIsNewFixedExpenseOpen(true); }} 
                className="flex-1 md:flex-none bg-red-600 text-white px-6 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 hover:bg-red-700 transition-all shadow-xl shadow-red-600/20 active:scale-95"
               >
                  <Plus size={18} /> Novo Gasto Fixo
               </button>
            </div>
          </div>

          <Card className="bg-white border border-gray-100 shadow-xl rounded-[32px] overflow-hidden">
             <div className="overflow-x-auto scrollbar-hide">
                <table className="w-full text-left border-collapse min-w-[900px]">
                   <thead>
                      <tr className="bg-gray-50 text-gray-400 text-[10px] font-black uppercase tracking-[0.2em] border-b border-gray-100">
                         <th className="px-8 py-6">Descrição</th>
                         <th className="px-8 py-6">Dia de Pagto</th>
                         <th className="px-8 py-6">Recorrência</th>
                         <th className="px-8 py-6 text-right">Valor Estimado</th>
                         <th className="px-8 py-6 text-right">Ação</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-gray-50">
                      {fixedExpenses.length > 0 ? fixedExpenses.map(expense => (
                        <tr key={expense.id} className="hover:bg-primary/5 transition-all group">
                           <td className="px-8 py-6">
                              <p className="text-xs font-black text-secondary uppercase tracking-tight">{expense.description}</p>
                              <Badge className="bg-blue-50 text-blue-600 border-none text-[8px] mt-1">
                                 {expense.category}
                              </Badge>
                           </td>
                           <td className="px-8 py-6">
                              <div className="flex items-center gap-2">
                                <Calendar size={14} className="text-red-500" />
                                <span className="text-[10px] font-black text-secondary uppercase tracking-widest">Todo dia {expense.dueDay}</span>
                              </div>
                           </td>
                           <td className="px-8 py-6">
                              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                                {expense.endDate ? `Até ${expense.endDate.split('-').reverse().join('/')}` : 'Mensal Vitalício'}
                              </span>
                           </td>
                           <td className="px-8 py-6 text-right">
                              <p className="font-black text-red-600 text-sm">R$ {expense.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                           </td>
                           <td className="px-8 py-6 text-right">
                              <button 
                                onClick={() => setSelectedFixedExpense(expense)}
                                className="p-2.5 text-gray-300 hover:text-primary hover:bg-white rounded-xl transition-all active:scale-90 shadow-sm border border-transparent hover:border-gray-100"
                              >
                                <ChevronRight size={20} />
                              </button>
                           </td>
                        </tr>
                      )) : (
                        <tr><td colSpan={5} className="px-8 py-24 text-center text-gray-200 font-black uppercase tracking-[0.3em] italic bg-transparent">Nenhum gasto fixo cadastrado</td></tr>
                      )}
                   </tbody>
                </table>
             </div>
          </Card>
        </>
      )}

      {/* MODAL DE NOVO/EDITAR PAGAMENTO */}
      {isNewPaymentOpen && (
        <NewPaymentModal 
          initialData={editingPaymentData}
          onClose={() => { setIsNewPaymentOpen(false); setEditingPaymentData(null); }} 
          onSave={handleSavePayment} 
        />
      )}

      {/* MODAL DE NOVO/EDITAR GASTO FIXO */}
      {isNewFixedExpenseOpen && (
        <NewFixedExpenseModal 
          initialData={editingFixedExpenseData}
          onClose={() => { setIsNewFixedExpenseOpen(false); setEditingFixedExpenseData(null); }} 
          onSave={handleSaveFixedExpense} 
        />
      )}

      {/* MODAL DE DETALHES DO PAGAMENTO */}
      {selectedPayment && (
        <PaymentDetailsModal 
          payment={selectedPayment} 
          onClose={() => setSelectedPayment(null)} 
          onDelete={() => handleDeletePayment(selectedPayment.id)}
          onEdit={() => handleEditClick(selectedPayment)}
          onViewFile={() => handleOpenFile(selectedPayment.paymentNum)}
          onLiquidate={() => handleLiquidatePayment(selectedPayment)}
        />
      )}

      {/* MODAL DE DETALHES DO GASTO FIXO */}
      {selectedFixedExpense && (
        <FixedExpenseDetailsModal 
          expense={selectedFixedExpense} 
          onClose={() => setSelectedFixedExpense(null)} 
          onDelete={() => handleDeleteFixedExpense(selectedFixedExpense.id)}
          onEdit={() => handleEditFixedClick(selectedFixedExpense)}
        />
      )}

      {/* VISUALIZADOR DE ARQUIVO */}
      {viewingFileUrl && (
        <div className="fixed inset-0 bg-black/95 z-[600] flex flex-col animate-in fade-in duration-300">
           <div className="p-6 flex justify-between items-center text-white bg-black/40">
              <div className="flex items-center gap-3">
                 <div className="p-2 bg-primary rounded-xl text-white"><ImageIcon size={20} /></div>
                 <span className="text-sm font-black uppercase tracking-widest">Comprovante de Lançamento</span>
              </div>
              <button onClick={() => setViewingFileUrl(null)} className="p-4 hover:bg-white/10 rounded-2xl transition-all"><X size={32} /></button>
           </div>
           <div className="flex-1 flex items-center justify-center p-4">
              <img src={viewingFileUrl} className="max-w-full max-h-full object-contain rounded-3xl shadow-2xl border-4 border-white/5 animate-in zoom-in-95" alt="Comprovante" />
           </div>
        </div>
      )}
    </div>
  );
};

const PaymentDetailsModal = ({ payment, onClose, onDelete, onEdit, onViewFile, onLiquidate }: any) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[400] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white border border-gray-100 rounded-[40px] w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[95vh]">
        <div className={`p-8 ${payment.status === 'PAID' ? 'bg-green-600' : 'bg-red-600'} text-white flex justify-between items-center relative overflow-hidden shrink-0`}>
            <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12"><Receipt size={120} /></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">Detalhes do Débito</h2>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-3">Identificador: {payment.paymentNum}</p>
            </div>
            <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl relative z-10 transition-all active:scale-90"><X size={28} /></button>
        </div>

        <div className="p-10 space-y-10 overflow-y-auto scrollbar-hide flex-1">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Favorecido</label>
                    <div className="flex items-center gap-4 text-secondary">
                        <div className="w-12 h-12 rounded-2xl bg-gray-50 border border-gray-100 flex items-center justify-center text-primary"><UserIcon size={24} /></div>
                        <span className="text-lg font-black uppercase leading-tight">{payment.clientName}</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Valor da Transação</label>
                    <div className="text-3xl font-black text-red-600 tracking-tighter">R$ {payment.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Descrição / Notas</label>
                    <p className="text-sm font-medium text-gray-500 leading-relaxed italic">"{payment.description || 'Nenhuma observação registrada.'}"</p>
                  </div>
              </div>

              <div className="space-y-6 bg-gray-50/50 p-8 rounded-[32px] border border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Método</span>
                    <span className="text-xs font-black text-secondary uppercase">{payment.method}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Vencimento</span>
                    <span className="text-xs font-black text-secondary">{payment.date.split('-').reverse().join('/')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Fatura Ref.</span>
                    <span className="text-xs font-black text-primary">{payment.invoiceNum}</span>
                  </div>
                  <div className="pt-6 border-t border-gray-100">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-3">Comprovante Digital</label>
                    <button 
                        onClick={onViewFile}
                        className="w-full h-24 bg-white border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center text-gray-300 gap-2 hover:bg-gray-50 hover:text-gray-400 transition-all cursor-pointer group active:scale-95"
                    >
                        <ImageIcon size={24} className="group-hover:scale-110 group-hover:text-primary transition-all" />
                        <span className="text-[9px] font-black uppercase tracking-widest">Ver Arquivo</span>
                    </button>
                  </div>
              </div>
            </div>

            <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
              {isConfirmingDelete ? (
                <div className="flex-1 flex gap-3 animate-in slide-in-from-bottom-2 duration-300">
                   <button 
                      onClick={() => setIsConfirmingDelete(false)} 
                      className="flex-1 py-3.5 bg-gray-100 text-gray-500 rounded-2xl font-black uppercase text-[10px] tracking-widest"
                   >
                      Não, Manter
                   </button>
                   <button 
                      onClick={onDelete} 
                      className="flex-[2] py-3.5 bg-red-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-red-600/20 active:scale-95"
                   >
                      SIM, EXCLUIR AGORA
                   </button>
                </div>
              ) : (
                <>
                  {payment.status === 'PENDING' && (
                    <button 
                      onClick={onLiquidate}
                      className="flex-1 py-3.5 bg-green-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-green-600/20 hover:bg-green-700 active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 size={16} /> Liquidar
                    </button>
                  )}
                  <button 
                    onClick={() => setIsConfirmingDelete(true)} 
                    className="flex-1 py-3.5 bg-red-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-red-700 transition-all flex items-center justify-center gap-2 active:scale-95 shadow-xl shadow-red-600/20"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                  <button 
                    onClick={onEdit}
                    className="flex-1 py-3.5 bg-gray-100 text-gray-500 border border-gray-200 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-200 hover:text-secondary transition-all flex items-center justify-center gap-2 active:scale-95"
                  >
                    <Edit3 size={16} /> Editar
                  </button>
                  <button 
                    onClick={onClose} 
                    className="flex-1 py-3.5 bg-[#00a8e0] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/30 hover:brightness-110 active:scale-95 transition-all"
                  >
                    Fechar
                  </button>
                </>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

const NewFixedExpenseModal = ({ onClose, onSave, initialData }: any) => {
  const isEditing = !!initialData;
  const [formData, setFormData] = useState({
    id: initialData?.id || '',
    description: initialData?.description || '',
    amount: initialData?.amount || '',
    dueDay: initialData?.dueDay || 1,
    category: initialData?.category || 'Infraestrutura',
    startDate: initialData?.startDate || new Date().toISOString().split('T')[0],
    endDate: initialData?.endDate || '',
    isRecurringIndefinitely: !initialData?.endDate
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description || formData.amount === '') {
      return;
    }
    const newExpense = {
      ...formData,
      id: formData.id || crypto.randomUUID(),
      amount: parseFloat(String(formData.amount).replace(',', '.')),
      endDate: formData.isRecurringIndefinitely ? null : formData.endDate,
      status: initialData?.status || 'ACTIVE',
      createdAt: initialData?.createdAt || new Date().toISOString()
    };
    onSave(newExpense);
  };

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[500] flex items-center justify-center p-4">
      <div className="bg-white border border-gray-100 rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        <div className={`p-8 ${isEditing ? 'bg-amber-500' : 'bg-red-600'} text-white flex justify-between items-center relative overflow-hidden shrink-0`}>
           <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12"><RotateCcw size={120} /></div>
           <div className="relative z-10">
              <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">{isEditing ? 'Editar Gasto Fixo' : 'Novo Gasto Fixo'}</h2>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-3">Despesas Recorrentes</p>
           </div>
           <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl relative z-10 transition-all active:scale-90"><X size={28} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto scrollbar-hide flex-1">
           <div className="space-y-4">
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Descrição do Gasto</label>
                 <div className="relative">
                    <Tag className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                    <input 
                      required 
                      autoFocus
                      className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                      placeholder="Ex: Aluguel do Galpão"
                      value={formData.description}
                      onChange={e => setFormData({...formData, description: e.target.value})}
                    />
                 </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor Mensal (R$)</label>
                    <div className="relative">
                       <DollarSign className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                       <input 
                        required 
                        type="number"
                        step="0.01"
                        className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-black text-secondary focus:border-red-500 transition-all shadow-sm" 
                        placeholder="0,00"
                        value={formData.amount}
                        onChange={e => setFormData({...formData, amount: e.target.value})}
                       />
                    </div>
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Dia do Pagamento</label>
                    <div className="relative">
                      <Calendar className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                      <input 
                        required 
                        type="number"
                        min="1"
                        max="31"
                        className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                        value={isNaN(formData.dueDay) ? '' : formData.dueDay}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          setFormData({...formData, dueDay: isNaN(val) ? '' as any : val});
                        }}
                      />
                    </div>
                 </div>
              </div>

              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Categoria</label>
                 <div className="relative">
                    <Building2 className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                    <input 
                      required 
                      className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                      placeholder="Ex: Infraestrutura, Pessoal, Software..."
                      value={formData.category}
                      onChange={e => setFormData({...formData, category: e.target.value})}
                    />
                 </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-3">
                  <input 
                    type="checkbox" 
                    id="indefinite"
                    className="w-5 h-5 rounded-lg border-gray-300 text-red-600 focus:ring-red-500"
                    checked={formData.isRecurringIndefinitely}
                    onChange={e => setFormData({...formData, isRecurringIndefinitely: e.target.checked})}
                  />
                  <label htmlFor="indefinite" className="text-xs font-bold text-secondary uppercase tracking-widest cursor-pointer">Recorrência Mensal Vitalícia</label>
                </div>

                {!formData.isRecurringIndefinitely && (
                  <div className="space-y-1.5 animate-in slide-in-from-top-2 duration-300">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Data Limite (Até quando?)</label>
                    <input 
                      required 
                      type="date"
                      className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                      value={formData.endDate}
                      onChange={e => setFormData({...formData, endDate: e.target.value})}
                    />
                  </div>
                )}
              </div>
           </div>
        </form>
        
        <div className="p-8 bg-gray-50 border-t border-gray-100 shrink-0">
           <div className="flex gap-4">
              <button type="button" onClick={onClose} className="flex-1 py-5 bg-white border border-gray-200 text-gray-400 rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-100 transition-all active:scale-95">Cancelar</button>
              <button type="button" onClick={handleSubmit} className={`flex-[2] py-5 ${isEditing ? 'bg-amber-500 shadow-amber-500/20' : 'bg-red-600 shadow-red-600/20'} text-white rounded-3xl font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 active:scale-95 hover:brightness-110 transition-all`}>
                 <Save size={18} /> {isEditing ? 'Salvar Alterações' : 'Salvar Gasto Fixo'}
              </button>
           </div>
        </div>
      </div>
    </div>
  );
};

const FixedExpenseDetailsModal = ({ expense, onClose, onDelete, onEdit }: any) => {
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[400] flex items-center justify-center p-4 animate-in fade-in duration-300">
      <div className="bg-white border border-gray-100 rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col">
        <div className="p-8 bg-red-600 text-white flex justify-between items-center relative overflow-hidden shrink-0">
            <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12"><RotateCcw size={120} /></div>
            <div className="relative z-10">
              <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">Gasto Fixo</h2>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-3">Recorrência Mensal</p>
            </div>
            <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl relative z-10 transition-all active:scale-90"><X size={28} /></button>
        </div>

        <div className="p-10 space-y-8 flex-1">
            <div className="space-y-6">
                <div>
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Descrição</label>
                  <p className="text-xl font-black text-secondary uppercase leading-tight">{expense.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Valor Mensal</label>
                    <div className="text-2xl font-black text-red-600 tracking-tighter">R$ {expense.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Dia do Pagto</label>
                    <div className="text-xl font-black text-secondary uppercase tracking-widest">Todo dia {expense.dueDay}</div>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 rounded-[24px] border border-gray-100 space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Categoria</span>
                    <span className="text-xs font-black text-secondary uppercase">{expense.category}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Início</span>
                    <span className="text-xs font-black text-secondary">{expense.startDate.split('-').reverse().join('/')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-gray-400 uppercase">Fim da Recorrência</span>
                    <span className="text-xs font-black text-secondary uppercase">
                      {expense.endDate ? expense.endDate.split('-').reverse().join('/') : 'Indeterminado'}
                    </span>
                  </div>
                </div>
            </div>

            <div className="pt-8 border-t border-gray-100 flex flex-col sm:flex-row gap-3">
              {isConfirmingDelete ? (
                <div className="flex-1 flex gap-3 animate-in slide-in-from-bottom-2 duration-300">
                   <button 
                      onClick={() => setIsConfirmingDelete(false)} 
                      className="flex-1 py-3.5 bg-gray-100 text-gray-500 rounded-2xl font-black uppercase text-[10px] tracking-widest"
                   >
                      Não, Manter
                   </button>
                   <button 
                      onClick={onDelete} 
                      className="flex-[2] py-3.5 bg-red-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-red-600/20 active:scale-95"
                   >
                      SIM, EXCLUIR
                   </button>
                </div>
              ) : (
                <>
                  <button 
                    onClick={() => setIsConfirmingDelete(true)} 
                    className="flex-1 py-3.5 bg-red-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-red-700 transition-all flex items-center justify-center gap-2 active:scale-95 shadow-xl shadow-red-600/20"
                  >
                    <Trash2 size={16} /> Excluir
                  </button>
                  <button 
                    onClick={onEdit}
                    className="flex-1 py-3.5 bg-gray-100 text-gray-500 border border-gray-200 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-200 hover:text-secondary transition-all flex items-center justify-center gap-2 active:scale-90"
                  >
                    <Edit3 size={16} /> Editar
                  </button>
                  <button 
                    onClick={onClose} 
                    className="flex-1 py-3.5 bg-[#00a8e0] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/30 hover:brightness-110 active:scale-95 transition-all"
                  >
                    Fechar
                  </button>
                </>
              )}
            </div>
        </div>
      </div>
    </div>
  );
};

const NewPaymentModal = ({ onClose, onSave, initialData }: any) => {
  const isEditing = !!initialData;
  const [formData, setFormData] = useState(() => ({
    id: initialData?.id || '',
    paymentNum: initialData?.paymentNum || `PAY-${Math.floor(Math.random() * 90000 + 10000)}`,
    clientName: initialData?.clientName || '',
    invoiceNum: initialData?.invoiceNum || '',
    method: initialData?.method || 'PIX',
    amount: initialData?.amount || '',
    date: initialData?.date || new Date().toISOString().split('T')[0],
    transactionId: initialData?.transactionId || `TXN-${Math.floor(Math.random() * 90000000 + 10000000)}`,
    description: initialData?.description || ''
  }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.clientName || formData.amount === '') {
      return;
    }
    const newPayment = {
      ...formData,
      id: formData.id || crypto.randomUUID(),
      amount: parseFloat(String(formData.amount).replace(',', '.')),
      status: initialData?.status || 'PENDING'
    };
    onSave(newPayment);
  };

  return (
    <div className="fixed inset-0 bg-secondary/90 backdrop-blur-sm z-[500] flex items-center justify-center p-4">
      <div className="bg-white border border-gray-100 rounded-[40px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        <div className={`p-8 ${isEditing ? 'bg-amber-500' : 'bg-red-600'} text-white flex justify-between items-center relative overflow-hidden shrink-0`}>
           <div className="absolute top-0 right-0 p-4 opacity-10 rotate-12"><Banknote size={120} /></div>
           <div className="relative z-10">
              <h2 className="text-2xl font-black uppercase tracking-tight italic leading-none">{isEditing ? 'Editar Lançamento' : 'Registrar Saída'}</h2>
              <p className="text-[10px] font-bold text-white/60 uppercase tracking-widest mt-3">Fluxo de Caixa Master</p>
           </div>
           <button onClick={onClose} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl relative z-10 transition-all active:scale-90"><X size={28} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6 overflow-y-auto scrollbar-hide flex-1">
           <div className="space-y-4">
              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Favorecido / Destino</label>
                 <div className="relative">
                    <UserIcon className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                    <input 
                      required 
                      autoFocus
                      className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                      placeholder="Ex: Amazon Web Services"
                      value={formData.clientName}
                      onChange={e => setFormData({...formData, clientName: e.target.value})}
                    />
                 </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor Bruto (R$)</label>
                    <div className="relative">
                       <DollarSign className="absolute left-5 top-1/2 -translate-y-1/2 text-red-500" size={18} />
                       <input 
                        required 
                        type="number"
                        step="0.01"
                        className="w-full bg-gray-50 border border-gray-100 p-5 pl-14 rounded-2xl outline-none font-black text-secondary focus:border-red-500 transition-all shadow-sm" 
                        placeholder="0,00"
                        value={formData.amount}
                        onChange={e => setFormData({...formData, amount: e.target.value})}
                       />
                    </div>
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Vencimento</label>
                    <input 
                      required 
                      type="date"
                      className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                      value={formData.date}
                      onChange={e => setFormData({...formData, date: e.target.value})}
                    />
                 </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Método de Pagto</label>
                    <div className="relative">
                      <select 
                        className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none font-bold text-secondary appearance-none cursor-pointer focus:border-red-500 shadow-sm"
                        value={formData.method}
                        onChange={e => setFormData({...formData, method: e.target.value})}
                      >
                         <option>PIX</option>
                         <option>Boleto</option>
                         <option>Transferência</option>
                         <option>Cartão Unidade</option>
                      </select>
                      <ChevronDown size={18} className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                 </div>
                 <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Doc / Fatura Ref.</label>
                    <div className="relative">
                       <Hash className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                       <input 
                        className="w-full bg-gray-50 border border-gray-100 p-5 pl-12 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all shadow-sm" 
                        placeholder="Ex: INV-001"
                        value={formData.invoiceNum}
                        onChange={e => setFormData({...formData, invoiceNum: e.target.value})}
                       />
                    </div>
                 </div>
              </div>

              <div className="space-y-1.5">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Descrição do Lançamento</label>
                 <textarea 
                   rows={3}
                   className="w-full bg-gray-50 border border-gray-100 p-5 rounded-2xl outline-none font-bold text-secondary focus:border-red-500 transition-all resize-none shadow-sm" 
                   placeholder="Detalhes adicionais sobre este débito..."
                   value={formData.description}
                   onChange={e => setFormData({...formData, description: e.target.value})}
                 />
              </div>
           </div>
        </form>
        
        <div className="p-8 bg-gray-50 border-t border-gray-100 shrink-0">
           <div className="flex gap-4">
              <button type="button" onClick={onClose} className="flex-1 py-5 bg-white border border-gray-200 text-gray-400 rounded-3xl font-black uppercase text-[10px] tracking-widest hover:bg-gray-100 transition-all active:scale-95">Cancelar</button>
              <button type="button" onClick={handleSubmit} className={`flex-[2] py-5 ${isEditing ? 'bg-amber-500 shadow-amber-500/20' : 'bg-red-600 shadow-red-600/20'} text-white rounded-3xl font-black uppercase text-xs tracking-widest shadow-xl flex items-center justify-center gap-3 active:scale-95 hover:brightness-110 transition-all`}>
                 <Save size={18} /> {isEditing ? 'Salvar Alterações' : 'Efetivar Lançamento'}
              </button>
           </div>
        </div>
      </div>
    </div>
  );
};
